import type { TradeLogicComparisonOperatorV2 } from '@/types/appRouter';
import { buildReferenceNodeInputState, type ReferenceNodeInputState, type TradeLogicMissingDataPolicy } from '../model';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryFeatureDto
} from '../registry';
import {
    buildConditionTemplateValueField,
    buildDefaultSchemaValues,
    schemaFieldValueToInputString,
    schemaValueToInputString
} from './schemaControlUtils';

export type DraftBuilderInputKind = 'entry' | 'exit';

export type DraftBuilderAddKind = 'condition' | 'action' | 'group';

export interface DraftBuilderInputState {
    actionTemplateValues: Record<string, string>;
    activeAddKind: DraftBuilderAddKind;
    conditionTemplateValues: Record<string, string>;
    featureSearch: string;
    filterOperators: Record<string, TradeLogicComparisonOperatorV2>;
    filterValues: Record<string, string>;
    localError: string | null;
    referenceError: string | null;
    referenceInputState: ReferenceNodeInputState;
    selectedActionTemplateKey: string;
    selectedConditionTemplateFeatureOptionKey: string;
    selectedConditionTemplateKey: string;
    selectedConditionTemplateOperator: string;
    selectedConditionTemplateValue: string;
    selectedFeatureKey: string;
    selectedFeatureOperator: string;
    selectedFeatureValue: string;
    selectedMissingDataPolicy: '' | TradeLogicMissingDataPolicy;
    selectorValues: Record<string, string>;
    showAdvancedFeatures: boolean;
}

export const createDraftBuilderInputState = (): DraftBuilderInputState => ({
    actionTemplateValues: {},
    activeAddKind: 'condition',
    conditionTemplateValues: {},
    featureSearch: '',
    filterOperators: {},
    filterValues: {},
    localError: null,
    referenceError: null,
    referenceInputState: buildReferenceNodeInputState(),
    selectedActionTemplateKey: '',
    selectedConditionTemplateFeatureOptionKey: '',
    selectedConditionTemplateKey: '',
    selectedConditionTemplateOperator: '',
    selectedConditionTemplateValue: '',
    selectedFeatureKey: '',
    selectedFeatureOperator: '=',
    selectedFeatureValue: '',
    selectedMissingDataPolicy: '',
    selectorValues: {},
    showAdvancedFeatures: false
});

export const createDraftBuilderInputStateByKind = (): Record<DraftBuilderInputKind, DraftBuilderInputState> => ({
    entry: createDraftBuilderInputState(),
    exit: createDraftBuilderInputState()
});

export const updateDraftBuilderFeatureSelection = ({
    featureKey,
    featuresByKey,
    state
}: {
    state: DraftBuilderInputState;
    featureKey: string;
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>;
}): DraftBuilderInputState => {
    const nextFeature = featureKey ? (featuresByKey[featureKey] ?? null) : null;

    return {
        ...state,
        filterOperators: Object.fromEntries((nextFeature?.filterFields ?? []).map((field) => [field.key, field.operators[0] ?? '='])),
        filterValues: nextFeature ? buildDefaultSchemaValues(nextFeature.filterFields) : {},
        localError: null,
        selectedFeatureKey: featureKey,
        selectedFeatureOperator: nextFeature?.allowedOperators[0] ?? '=',
        selectedFeatureValue: '',
        selectedMissingDataPolicy: '',
        selectorValues: nextFeature ? buildDefaultSchemaValues(nextFeature.selectorFields) : {}
    };
};

export const updateDraftBuilderActionTemplateSelection = ({
    actionTemplatesByKey,
    state,
    templateKey
}: {
    state: DraftBuilderInputState;
    templateKey: string;
    actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>;
}): DraftBuilderInputState => {
    const nextTemplate = templateKey ? (actionTemplatesByKey[templateKey] ?? null) : null;

    return {
        ...state,
        actionTemplateValues: buildDefaultSchemaValues(nextTemplate?.parameterFields ?? []),
        localError: null,
        selectedActionTemplateKey: templateKey
    };
};

export const updateDraftBuilderConditionTemplateSelection = ({
    conditionTemplatesByKey,
    state,
    templateKey
}: {
    state: DraftBuilderInputState;
    templateKey: string;
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>;
}): DraftBuilderInputState => {
    const nextTemplate = templateKey ? (conditionTemplatesByKey[templateKey] ?? null) : null;
    const nextFeatureOption = nextTemplate?.contextFeatureOptions[0] ?? null;
    const nextValueField = buildConditionTemplateValueField(nextTemplate);

    return {
        ...state,
        conditionTemplateValues: buildDefaultSchemaValues(nextTemplate?.parameterFields ?? []),
        localError: null,
        selectedConditionTemplateFeatureOptionKey: nextFeatureOption?.optionKey ?? '',
        selectedConditionTemplateKey: templateKey,
        selectedConditionTemplateOperator: nextFeatureOption?.defaultOperator ?? nextTemplate?.allowedOperators[0] ?? '=',
        selectedConditionTemplateValue: nextValueField
            ? schemaFieldValueToInputString(nextValueField, nextValueField.defaultValue ?? nextFeatureOption?.defaultValue ?? null)
            : schemaValueToInputString(nextFeatureOption?.defaultValue ?? null)
    };
};

export const updateDraftBuilderConditionTemplateFeatureOptionSelection = ({
    state,
    template,
    optionKey
}: {
    state: DraftBuilderInputState;
    template: TradeLogicRegistryConditionTemplateDto | null;
    optionKey: string;
}): DraftBuilderInputState => {
    const nextFeatureOption = template?.contextFeatureOptions.find((option) => option.optionKey === optionKey) ?? null;
    const nextValueField = buildConditionTemplateValueField(template);

    return {
        ...state,
        localError: null,
        selectedConditionTemplateFeatureOptionKey: optionKey,
        selectedConditionTemplateOperator: nextFeatureOption?.defaultOperator ?? template?.allowedOperators[0] ?? '=',
        selectedConditionTemplateValue: nextValueField
            ? schemaFieldValueToInputString(nextValueField, nextValueField.defaultValue ?? nextFeatureOption?.defaultValue ?? null)
            : schemaValueToInputString(nextFeatureOption?.defaultValue ?? null)
    };
};
